import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { billingVerify } from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/lib/AuthContext";

export default function BillingCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const reference = params.get("reference") || params.get("trxref");
  const [state, setState] = useState({ status: "verifying" });

  useEffect(() => {
    if (!reference) {
      setState({ status: "error", message: "Missing transaction reference." });
      return;
    }
    let cancelled = false;
    billingVerify(reference)
      .then(async (data) => {
        if (cancelled) return;
        if (data?.status === "success") {
          try { await refresh?.(); } catch { /* no-op */ }
          setState({ status: "success", plan: data.plan });
        } else {
          setState({ status: "error", message: "Payment wasn't successful. You haven't been charged." });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: "error",
          message: formatApiErrorDetail(err?.response?.data?.detail) || "Could not verify your payment.",
        });
      });
    return () => { cancelled = true; };
  }, [reference, refresh]);

  return (
    <div className="min-h-screen fc-app-bg grid place-items-center px-4" data-testid="billing-callback">
      <div className="fc-card p-8 max-w-md w-full text-center">
        {state.status === "verifying" && (
          <>
            <Loader2 className="w-8 h-8 mx-auto text-sage-700 animate-spin" />
            <h2 className="fc-heading text-lg text-[#1C1C1C] mt-4">Confirming your payment…</h2>
            <p className="text-sm text-[#78716C] mt-2">This usually takes a second or two.</p>
          </>
        )}
        {state.status === "success" && (
          <>
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/30 grid place-items-center mx-auto">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <h2 className="fc-heading text-lg text-[#1C1C1C] mt-4">Payment successful</h2>
            <p className="text-sm text-[#57534E] mt-2">
              Your {state.plan === "single" ? "Single Project" : state.plan === "pro" ? "Pro" : "Unlimited"} plan is now active.
            </p>
            <Button
              data-testid="billing-callback-continue"
              onClick={() => navigate("/projects", { replace: true })}
              className="mt-6 bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] inline-flex items-center gap-1.5"
            >
              Open my projects <ArrowRight className="w-4 h-4" />
            </Button>
          </>
        )}
        {state.status === "error" && (
          <>
            <div className="w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/30 grid place-items-center mx-auto">
              <AlertTriangle className="w-5 h-5 text-amber-700" />
            </div>
            <h2 className="fc-heading text-lg text-[#1C1C1C] mt-4">We couldn't verify the payment</h2>
            <p className="text-sm text-[#57534E] mt-2">{state.message}</p>
            <div className="mt-6 flex items-center justify-center gap-2">
              <Button
                data-testid="billing-callback-retry"
                onClick={() => navigate("/pricing", { replace: true })}
                className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E]"
              >
                Back to pricing
              </Button>
              <button
                onClick={() => navigate("/projects", { replace: true })}
                className="text-sm text-[#78716C] hover:text-[#1C1C1C] underline underline-offset-2"
              >
                Go to projects
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
